import React, { useState } from "react";
import {
  FaReact,
  FaNodeJs,
  FaAngular,
  FaDatabase,
  FaBootstrap,
  FaExternalLinkAlt,
  FaAndroid,
  FaJava,
} from "react-icons/fa";
import { SiTypescript, SiFirebase } from "react-icons/si";
import { TbApi } from "react-icons/tb";
import { motion } from "framer-motion";

const mobile = require("../../assets/4781.jpg");
const ecommerce1 = require("../../assets/4910241.jpg");
const ecommerce2 = require("../../assets/4934538.jpg");

const projects = [
  {
    id: 1,
    title: "E-commerce Store",
    description:
      "The E-commerce Store is a full-stack application for online shopping. Users can browse products, manage their cart, and checkout securely. Built with React and Node.js, it uses RESTful APIs for dynamic data.",
    caption: "A full-stack e-commerce app for online shopping.",
    link: "https://github.com/Code2voyager/React-Watch-ECommerce",
    techStack: ["MongoDB","React","Node.js"],
    image: ecommerce1,
  },
  {
    id: 2,
    title: "Interview Scheduling Dashboard (In Progress)",
    caption:
      "A role-based dashboard for recruiters and candidates to manage, book, and track interviews.",
    description:
      "A role-based dashboard for recruiters and candidates to manage, book, and track interviews. Actively developed to showcase Java backend and Angular frontend skills with PostgreSQL, Spring Boot, and Calendly integration for smart scheduling.",
    link: "https://github.com/Code2voyager/interview-scheduling-dashboard",
    techStack: ["Angular 16", "Springboot", "PostgreSql"],
    image: ecommerce2,
  },
  {
    id: 3,
    title: "Android Ecommerce",
    caption:
      "A feature-rich front-end UI for an e-commerce platform built with Android, Java, and Firebase. It offers a seamless shopping experience, allowing users to browse products, manage their cart, and make purchases with real-time data synchronization.",
    description:
      "The E-commerce Platform UI is a dynamic front-end application that showcases an engaging user interface for online shopping. Developed using Angular and TypeScript, this project emphasizes a responsive design that enhances user experience.",
    link: "https://github.com/Code2voyager/Android-Ecommerce",
    techStack: ["Android", "Java", "Firebase"],
    image: mobile,
  },
];

const getTechIcon = (tech) => {
  switch (tech) {
    case "React":
      return <FaReact size={24} className="text-blue-500" />;
    case "Node.js":
      return <FaNodeJs size={24} className="text-green-500" />;
    case "Angular":
      return <FaAngular size={24} className="text-pink-600" />;
    case "MongoDB":
      return <FaDatabase size={24} className="text-green-600" />;
    case "Bootstrap":
      return <FaBootstrap size={24} className="text-purple-600" />;
    case "TypeScript":
      return <SiTypescript size={24} className="text-blue-600" />;
    case "API":
      return <TbApi size={24} className="text-blue-600" />;
    case "Android":
      return <FaAndroid size={24} className="text-green-600" />;
    case "Java":
      return <FaJava size={24} className="text-red-700" />;
    case "Firebase":
      return <SiFirebase size={24} className="text-yellow-500" />;
    default:
      return null;
  }
};

const listVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.06 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

function Projects() {
  const [flipped, setFlipped] = useState({});

  const handleMouseEnter = (id) => {
    setFlipped((prev) => ({ ...prev, [id]: true }));
  };

  const handleMouseLeave = (id) => {
    setFlipped((prev) => ({ ...prev, [id]: false }));
  };

  return (
    <>
      <div id="Projects" className="flex items-center justify-center">
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6 lg:p-12 mx-auto"
          variants={listVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.12 }}
        >
          {projects.map((project) => (
            <motion.div
              key={project.id}
              className="relative w-full h-full flex justify-center items-center"
              onMouseEnter={() => handleMouseEnter(project.id)}
              onMouseLeave={() => handleMouseLeave(project.id)}
              style={{ perspective: 1000, cursor: "pointer" }}
              variants={cardVariants}
            >
              <motion.div
                className="relative w-full"
                animate={{ rotateY: flipped[project.id] ? 180 : 0 }}
                transition={{ duration: 0.6 }}
                style={{ transformStyle: "preserve-3d" }}
              >
                <motion.div style={{ backfaceVisibility: "hidden" }}>
                  <div className="max-w-sm rounded-xl shadow-xl bg-surface-card/95 border border-violet-400/15 backdrop-blur-md transform transition-all duration-500 hover:scale-[1.02] hover:border-sky-400/30 hover:shadow-[0_20px_50px_-20px_rgba(56,189,248,0.15)]">
                    <div
                      className="h-48 bg-cover bg-center rounded-t-lg"
                      style={{ backgroundImage: `url(${project.image})` }}
                    ></div>
                    <div className="px-6 py-4">
                      <h3 className="text-xl font-semibold text-slate-100 mb-2 tracking-tight">
                        {project.title}
                      </h3>
                      <div className="px-2 pt-4 pb-2">
                        {project.techStack.map((tech) => (
                          <span
                            key={tech}
                            className="inline-block rounded-full px-3 py-1 text-sm font-medium text-slate-200 mr-2 mb-2 border border-violet-400/15 bg-white/[0.04]"
                          >
                            {getTechIcon(tech)}{" "}
                            <span className="ml-2">{tech}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute inset-0 bg-surface-card/98 text-slate-100 border border-violet-400/20 rounded-xl shadow-xl p-6 flex flex-col items-center justify-center"
                  style={{
                    transform: "rotateY(180deg)",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <p className="text-base md:text-lg text-center text-slate-200/95 leading-relaxed">{project.description}</p>
                  <div className="mt-5">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sky-300 font-semibold hover:text-sky-200 transition-colors"
                    >
                      Explore more on GitHub{" "}
                      <FaExternalLinkAlt className="ml-2" />
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </>
  );
}

export default Projects;
